import styled from '@emotion/styled';
import { LinearProgress } from '@mui/material';
import { COLORS } from './ScoreBadge';
import type { RunStatus } from './types';

// ─── Progress Bar ───────────────────────────────────────────────────────────

interface MessageTestingProgressBarProps {
  status: RunStatus;
  progress: { completed: number; total: number };
  error?: string | null;
}

export function MessageTestingProgressBar({
  status,
  progress,
  error,
}: MessageTestingProgressBarProps): React.JSX.Element | null {
  const progressPercent = progress.total > 0 ? (progress.completed / progress.total) * 100 : 0;

  return (
    <>
      {(status === 'connecting' || status === 'processing') && (
        <ProgressSection>
          <ProgressLabel>
            {status === 'connecting'
              ? 'Connecting...'
              : progress.completed > 0 && progress.completed === progress.total
                ? `Complete — ${progress.total} pairs processed`
                : progress.total === 0
                  ? 'Processing...'
                  : `Processing ${progress.completed} / ${progress.total} pairs`}
          </ProgressLabel>
          <LinearProgress variant="determinate" value={progressPercent} />
        </ProgressSection>
      )}

      {status === 'analyzing' && (
        <ProgressSection>
          <ProgressLabel>Analyzing results across segments and categories...</ProgressLabel>
          <LinearProgress variant="indeterminate" />
        </ProgressSection>
      )}

      {status === 'failed' && <ErrorBanner>Simulation failed: {error ?? 'Unknown error'}</ErrorBanner>}
    </>
  );
}

// ─── Styled ─────────────────────────────────────────────────────────────────

const ProgressSection = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
`;

const ProgressLabel = styled.div`
  font-size: 12px;
  color: #666;
  margin-bottom: 6px;
`;

const ErrorBanner = styled.div`
  padding: 12px 16px;
  background: ${COLORS.red.bg};
  color: ${COLORS.red.dark};
  font-size: 13px;
  border-bottom: 1px solid ${COLORS.red.border};
`;
